// src/components/BankPosting/components/BankPostingInvoiceList.jsx
// ============================================================================
// BLOCK BP.CMP.003 — Shared invoice list panel
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders invoice rows for Suggested Invoices and Open Invoices panels
// - Keeps a single display formatter so invoice text is consistent everywhere
//
// DISPLAY FORMAT
// - Inv Balance - Inv# - Inv Date - MASID - Customer Name
// - Example:
//      $1000.00 - 0754434 - 02-01-2026 - BAPTISCA - Baptist Hospital CA
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  buildInvoicePrimary,
  resolveSuggestionOrigin,
  resolveSuggestionOriginLabel,
} from '../utils/invoiceDisplayFormatters.js';

// ============================================================================
// BLOCK BP.CMP.003.01 — Display formatting helpers
// ----------------------------------------------------------------------------
// NOTES
// - Uses safe fallbacks because backend row shapes can vary slightly between
//   suggested/open/selected invoice payloads.
// - Prefers invoice_date, but falls back to due_date if invoice_date is absent.
// ============================================================================
function trimStr(value) {
  return String(value ?? '').trim();
}

function normalizeCustomerName(value) {
  return trimStr(value)
    .replace(/(?:\.)?\s*payables\b/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/[.\s]+$/g, '')
    .trim();
}

function resolveMasid(row) {
  return trimStr(row?.masid || row?.mas_id || row?.payment_id || row?.remittance_id);
}

function resolveMasCustomerId(row) {
  return trimStr(row?.mas_customer_id || resolveMasid(row));
}

function resolveBillToName(row) {
  return normalizeCustomerName(row?.bill_to_name || row?.customer_name || '');
}

function buildSuggestedSecondaryDetails(row) {
  const masCustomerId = resolveMasCustomerId(row);
  const billToName = resolveBillToName(row);

  return [masCustomerId, billToName].filter(Boolean).join(' - ');
}

export default function BankPostingInvoiceList({
  title,
  rows,
  selectedKeys,
  onToggle,
  onAssignedElsewhereClick,
  minHeight = 220,
  hideTitle = false,
  kind = 'generic',
  pagination,
  onPageChange,
  maxHeight = 300,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeSelectedKeys = Array.isArray(selectedKeys) ? selectedKeys : [];
  const shouldShowPagination = kind !== 'suggested' && Boolean(pagination?.total_pages > 1);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        minHeight,
        p: 1,
        bgcolor: 'background.paper',
      }}
    >
      {!hideTitle && Boolean(title) && (
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}

      <List dense disablePadding sx={{ maxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
        {safeRows.map((row) => {
          // --------------------------------------------------------------------------
          // Assignment state
          //
          // getTransactionSuggestions.js determines assignment using canonical
          // invoice number. Suggested and Open rows receive these same flags.
          // --------------------------------------------------------------------------
          const isAssignedToCurrent = Boolean(row?.is_selected_on_current_transaction);

          const isAssignedElsewhere = Boolean(
            row?.is_assigned_elsewhere ||
            row?.is_globally_linked ||
            (trimStr(row?.linked_bank_transaction_id) && !isAssignedToCurrent)
          );

          const isAssigned = Boolean(
            row?.is_assigned || isAssignedToCurrent || isAssignedElsewhere
          );

          const source = trimStr(row?.invoice_source || row?.source).toLowerCase();
          const lacksExactLokaMatch =
            source.startsWith('sage') && row?.has_loka_invoice_match === false;
          const isSuggestedList = kind === 'suggested';
          const isOpenList = kind === 'open';
          const allowManualOpenAssignment = Boolean(row?.allow_manual_open_assignment);
          const showUnassignableErrorStyling =
            lacksExactLokaMatch && !isOpenList && !allowManualOpenAssignment;
          const disableForNoExactLokaMatch = showUnassignableErrorStyling;

          // Assigned means unavailable regardless of which parent owns it.
          const isSelectionDisabled = isAssigned || disableForNoExactLokaMatch;
          const suggestionOrigin = isSuggestedList ? resolveSuggestionOrigin(row) : '';
          const isOpenFallbackSuggested = isSuggestedList && suggestionOrigin === 'open-fallback';
          const isLokaWeakSuggested = isSuggestedList && suggestionOrigin === 'loka-weak';
          const scoreValue = Number(row?.score || 0);
          const isStrongSuggestedScore = isSuggestedList && scoreValue > 80;
          const isMediumSuggestedScore = isSuggestedList && scoreValue >= 45 && scoreValue <= 80;
          const isLowSuggestedScore = isSuggestedList && scoreValue < 45;
          const isLokaSuggested =
            isSuggestedList && !isOpenFallbackSuggested && !isLokaWeakSuggested;
          const suggestedSecondaryDetails = isSuggestedList
            ? buildSuggestedSecondaryDetails(row)
            : '';
          const assignedTransactionCaption = trimStr(row?.assigned_transaction_description);
          const secondaryText = isAssigned
            ? trimStr(
                row?.unassignable_reason ||
                  (isAssignedToCurrent
                    ? 'Already assigned to this bank transaction'
                    : assignedTransactionCaption
                      ? `Assigned in bank transaction ${assignedTransactionCaption}`
                      : 'Already assigned to another bank transaction')
              )
            : lacksExactLokaMatch && !allowManualOpenAssignment
              ? trimStr(row?.unassignable_reason || 'No exact LOKA invoice match')
              : isSuggestedList
                ? `${resolveSuggestionOriginLabel(row)}${row.score ? ` | Score: ${row.score}` : ''}${suggestedSecondaryDetails ? ` - ${suggestedSecondaryDetails}` : ''}`
                : row.score
                  ? `Score: ${row.score}`
                  : row.invoice_source;

          return (
            <ListItemButton
              key={row.invoice_key}
              dense
              onClick={() => {
                if (isAssigned) {
                  // If it belongs to another bank transaction,
                  // optionally allow navigation to that transaction.
                  if (isAssignedElsewhere) {
                    onAssignedElsewhereClick?.(row);
                  }

                  // Never allow an assigned invoice to be selected again.
                  return;
                }

                if (!isSelectionDisabled) {
                  onToggle(row.invoice_key);
                }
              }}
              sx={{
                py: 0.125,
                px: 0.5,
                alignItems: 'flex-start',
                opacity: isSelectionDisabled ? 0.55 : 1,
                borderLeft: isSuggestedList ? '3px solid' : 'none',
                borderLeftColor: showUnassignableErrorStyling
                  ? 'error.main'
                  : isStrongSuggestedScore
                    ? 'success.main'
                    : isMediumSuggestedScore || isOpenFallbackSuggested || isLokaWeakSuggested
                      ? 'warning.main'
                      : isLowSuggestedScore
                        ? 'error.main'
                        : isLokaSuggested
                          ? 'success.main'
                          : 'transparent',
                bgcolor: isAssigned
                  ? 'action.hover'
                  : showUnassignableErrorStyling
                    ? 'rgba(244, 67, 54, 0.12)'
                    : isStrongSuggestedScore
                      ? 'rgba(46, 125, 50, 0.12)'
                      : isMediumSuggestedScore || isOpenFallbackSuggested || isLokaWeakSuggested
                        ? 'rgba(255, 193, 7, 0.22)'
                        : isLowSuggestedScore
                          ? 'rgba(244, 67, 54, 0.12)'
                          : isLokaSuggested
                            ? 'rgba(46, 125, 50, 0.12)'
                            : 'transparent',
              }}
            >
              <Checkbox
                edge="start"
                checked={safeSelectedKeys.includes(row.invoice_key)}
                tabIndex={-1}
                disableRipple
                disabled={isSelectionDisabled}
                onClick={(e) => e.stopPropagation()}
                onChange={() => {
                  if (!isSelectionDisabled) {
                    onToggle(row.invoice_key);
                  }
                }}
                sx={{ pt: 0.25 }}
              />
              <ListItemText
                primary={buildInvoicePrimary(row, kind)}
                secondary={secondaryText}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: {
                    color: isSelectionDisabled ? 'text.disabled' : 'text.primary',
                    textDecoration: isAssigned ? 'line-through' : 'none',
                  },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: {
                    color: showUnassignableErrorStyling
                      ? 'error.light'
                      : isStrongSuggestedScore
                        ? 'success.light'
                        : isMediumSuggestedScore || isOpenFallbackSuggested || isLokaWeakSuggested
                          ? 'warning.light'
                          : isLowSuggestedScore
                            ? 'error.light'
                            : 'text.secondary',
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {shouldShowPagination && (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 1 }}
        >
          <Typography variant="caption">
            Page {pagination.page} of {pagination.total_pages} ({pagination.total_rows} rows)
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange?.(pagination.page - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => onPageChange?.(pagination.page + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

BankPostingInvoiceList.propTypes = {
  title: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.shape({ invoice_key: PropTypes.string.isRequired })).isRequired,
  selectedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onAssignedElsewhereClick: PropTypes.func,
  minHeight: PropTypes.number,
  hideTitle: PropTypes.bool,
  kind: PropTypes.oneOf(['generic', 'suggested', 'open']),
  pagination: PropTypes.shape({
    page: PropTypes.number,
    total_pages: PropTypes.number,
    total_rows: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  maxHeight: PropTypes.number,
};

BankPostingInvoiceList.defaultProps = {
  title: '',
  minHeight: 220,
  hideTitle: false,
  onAssignedElsewhereClick: null,
  kind: 'generic',
  pagination: null,
  onPageChange: null,
  maxHeight: 300,
};
